import { z } from 'zod';
/**
 * Rejecting a submission.
 *
 * Note what is NOT here: an `onReject` hook on `StepTypeSpec`. `contract.ts` gives a step
 * type `onApprove` and nothing else, and rejection is deliberately authored in this one
 * module instead. Approval effects are type-specific — routing a file, minting a credential
 * — while rejection does the same thing for every type: tell the person what to fix and put
 * the step back in front of them. Adding an `onReject` to the contract would need an ADR.
 */
/**
 * A CLOSED vocabulary, because this code has two jobs free text cannot do: it drives the
 * sentence the candidate reads, and it selects the email fragment. HR's own words go in
 * `note`, which is shown to nobody but HR until someone decides otherwise.
 */
export const rejectionReasonSchema = z.enum([
    'unreadable',
    'wrong-document',
    'expired-document',
    'incomplete',
    'name-mismatch',
    'other',
]);
/** The most HR can write to the candidate when sending a step back. */
export const CANDIDATE_COMMENT_MAX = 500;
export const rejectionSchema = z.object({
    reason: rejectionReasonSchema,
    /** HR's own words, for HR. Never enters an audit payload — see reviewAudit in the API. */
    note: z.string().max(1000).default(''),
    /**
     * HR's own words, FOR THE CANDIDATE: optional, plain text, shown under the reason sentence in the
     * portal and in the rejection email. Deliberately a separate field from `note` — `note` is internal
     * and stays so. Free text can carry personal or health details, so like `note` it never enters an
     * audit payload. Empty means none.
     */
    candidateComment: z.string().trim().max(CANDIDATE_COMMENT_MAX).default(''),
});
/**
 * `satisfies Record<RejectionReason, …>` is the point: adding a reason to the enum is a
 * compile error until its label and its person-facing sentence both exist. A reason with no
 * sentence would reach a candidate as a blank explanation of why their work came back.
 */
export const rejectionPresenters = {
    unreadable: {
        label: 'Cannot be read',
        personFacing: 'We could not read the file clearly. Could you send it again — a photo taken in good light usually does it.',
    },
    'wrong-document': {
        label: 'Wrong document',
        personFacing: 'This looks like a different document from the one we need here. Could you check and send that one.',
    },
    'expired-document': {
        label: 'Out of date',
        personFacing: 'This one has expired. Could you send the current version.',
    },
    incomplete: {
        label: 'Incomplete',
        personFacing: 'Part of this is missing — a page, a signature or a date. Could you send a complete copy.',
    },
    'name-mismatch': {
        label: 'Name does not match',
        personFacing: 'The name on this does not match our records. That is often just a maiden name or a middle initial — give our office a call and we will sort it out.',
    },
    other: {
        label: 'Something else',
        personFacing: 'We need something changed on this one. Our office will explain what.',
    },
};
export function presentRejection(reason) {
    return rejectionPresenters[reason];
}
export function isRejectionReason(value) {
    return Object.prototype.hasOwnProperty.call(rejectionPresenters, value);
}
/**
 * What the CANDIDATE reads for a stored rejection reason.
 *
 * The instance stores the reason CODE, deliberately, so the wording can be improved without
 * rewriting stored records. That only works if every surface resolves the code through this
 * one function — the portal was rendering the raw code, so someone whose document came back
 * was told, in full, "unreadable".
 *
 * An unrecognised value falls back rather than rendering blank or raw. Two real cases reach
 * this: a record written before the vocabulary existed, and a code added to the catalog that
 * a deployed web bundle has not shipped yet. Neither should show a candidate an internal
 * token, and neither should show them nothing at all — a warning bar with no words is worse
 * than a plain sentence, because they cannot tell whether something failed to load.
 */
export function rejectionSentenceFor(reason) {
    if (reason === null || reason.trim() === '')
        return null;
    if (isRejectionReason(reason))
        return rejectionPresenters[reason].personFacing;
    return rejectionPresenters.other.personFacing;
}
/**
 * What a rejection causes.
 *
 * One effect: tell them. The step going back to `rejected` is a state change the API makes,
 * not an effect — effects are things that happen OUTSIDE this database, and the queue must
 * not be responsible for a transition that has to be atomic with the write.
 */
export function rejectionEffects(personId) {
    return [
        {
            kind: 'send-email',
            personId,
            template: 'submission-rejected',
            tokenPurpose: 'portal-session',
            communicationId: null,
        },
    ];
}
//# sourceMappingURL=review.js.map